import { pointerWithin } from "@dnd-kit/core";

import { isContainerType } from "../../utils/planNodeUtils";

// Pure drag-and-drop logic for SessionPlanDisplay, split out so it can be
// unit-tested without importing the component tree (which pulls in ESM-only
// LLM SDKs jest won't transform).

// Container node types can hold children even while empty — an empty Group
// still renders as an expandable block with an "Add step" affordance and
// accepts nest-into drops, so user-created groups aren't dead ends (issue #44).
// Re-exported from planNodeUtils so the editor and the plan normaliser agree on
// what may hold children.
export { isContainerType };

// Custom collision algorithm: prefer pointerWithin (single innermost match
// against non-overlapping bounds) and tiebreak by smallest rect area. The
// design intent is that bounds never actually overlap, so the tiebreak is
// belt-and-braces against rounding errors at boundaries.
export const dndCollision = (args) => {
  const matches = pointerWithin(args);
  if (matches.length <= 1) return matches;
  return [...matches]
    .sort((a, b) => {
      const ra = args.droppableRects.get(a.id);
      const rb = args.droppableRects.get(b.id);
      const aArea = ra ? ra.width * ra.height : Infinity;
      const bArea = rb ? rb.width * rb.height : Infinity;
      return aArea - bArea;
    })
    .slice(0, 1);
};

// Resolve which `(parentIndexPath, insertIndex)` to call movePlanNode with,
// from an onDragEnd / onDragMove event. Returns null if the drop is a no-op.
// `into: true` marks a nest-into-group drop (used by the live drop hint).
export const resolveDropTarget = (event) => {
  const { active, over, activatorEvent } = event;
  if (!active || !over) return null;
  const activeData = active.data?.current;
  const overData = over.data?.current;
  if (!activeData || !overData) return null;
  const fromIndexPath = activeData.indexPath;
  if (!Array.isArray(fromIndexPath)) return null;

  if (overData.kind === "gap") {
    return {
      fromIndexPath,
      toParentIndexPath: overData.parentIndexPath,
      toIndex: overData.insertIndex,
      into: false,
    };
  }

  if (overData.kind === "row") {
    const targetIndexPath = overData.indexPath;
    if (!Array.isArray(targetIndexPath)) return null;
    // Self-drop on the same row is a no-op.
    if (
      fromIndexPath.length === targetIndexPath.length &&
      fromIndexPath.every((s, i) => s === targetIndexPath[i])
    ) {
      return null;
    }
    const overRect = over.rect;
    if (!overRect) return null;
    // Current pointer Y = where the finger / cursor started (activatorEvent)
    // plus how far the drag has translated (event.delta). Using activator
    // alone gives the initial touchdown coordinate, so short drags band
    // against the original position and resolve to a no-op insert index.
    // Fallback to the ghost's centre (translated rect) for keyboard drags
    // where there's no pointer event.
    const activatorY = activatorEvent?.clientY;
    const deltaY = event.delta?.y ?? 0;
    const translated = active.rect?.current?.translated;
    const pointerY =
      activatorY != null
        ? activatorY + deltaY
        : translated
          ? translated.top + translated.height / 2
          : null;
    if (pointerY == null) return null;
    const y = pointerY - overRect.top;
    const relative = y / overRect.height;
    const targetParent = targetIndexPath.slice(0, -1);
    const targetIdx = targetIndexPath[targetIndexPath.length - 1];

    if (overData.isGroup) {
      // Pixel-clamped edge bands (was 25%/75% of rect height). Percentage
      // bands broke on EXPANDED groups, whose droppable rect spans header +
      // children: 25% of a tall rect put the entire header inside the
      // "insert above" band, so nesting via the header was impossible.
      // Clamped edges keep collapsed rows behaving as before (~10px edges
      // on a 40px header) while tall rects resolve to "into" everywhere
      // except a thin strip at each edge.
      const edge = Math.min(Math.max(overRect.height * 0.25, 8), 20);
      if (y < edge) {
        return {
          fromIndexPath,
          toParentIndexPath: targetParent,
          toIndex: targetIdx,
          into: false,
        };
      }
      if (y > overRect.height - edge) {
        return {
          fromIndexPath,
          toParentIndexPath: targetParent,
          toIndex: targetIdx + 1,
          into: false,
        };
      }
      // Nest into the group as last child.
      return {
        fromIndexPath,
        toParentIndexPath: targetIndexPath,
        toIndex: Number.MAX_SAFE_INTEGER, // clamped by movePlanNode
        into: true,
      };
    }

    return {
      fromIndexPath,
      toParentIndexPath: targetParent,
      toIndex: relative < 0.5 ? targetIdx : targetIdx + 1,
      into: false,
    };
  }

  return null;
};
